import { Trans, useTranslation } from 'react-i18next'
import { styled } from 'styled-components'

import { ExplicableRule } from '@/components/conversation/Explicable'
import { Condition } from '@/components/EngineValue/Condition'
import Value from '@/components/EngineValue/Value'
import {
	Accordion,
	Body,
	CircledPlusIcon,
	Container,
	Emoji,
	ExternalLinkIcon,
	H2,
	H4,
	InfoButton,
	Item,
	Li,
	Link,
	Message,
	Strong,
	StyledLink,
	Ul,
} from '@/design-system'
import ItemTitle from '@/pages/simulateurs/comparaison-statuts/components/ItemTitle'
import WarningTooltip from '@/pages/simulateurs/comparaison-statuts/components/WarningTooltip'
import { EngineComparison } from '@/pages/simulateurs/comparaison-statuts/EngineComparison'

import DetailsRowCards from './DetailsRowCards'
import RevenuTable from './RevenuTable'

export const Comparaison = ({
	namedEngines,
	expandRevenuSection = false,
}: {
	namedEngines: EngineComparison
	expandRevenuSection?: boolean
	withStatusCards?: boolean
}) => {
	const régimeTIComparé = namedEngines.some(
		(namedEngine) => namedEngine.name === 'EI' || namedEngine.name === 'EURL'
	)
	const { t } = useTranslation()

	return (
		<Container
			backgroundColor={(theme) =>
				theme.darkMode
					? theme.colors.extended.dark[800]
					: theme.colors.bases.primary[200]
			}
		>
			{régimeTIComparé && (
				<StyledMessage type="error">
					<Body>
						<Emoji emoji="⚠️" />{' '}
						<Strong>
							<Trans i18nKey="pages.assistants.choix-statut.warning.réforme.texte">
								La{' '}
								<Link
									href="https://www.urssaf.fr/accueil/independant/comprendre-payer-cotisations/reforme-cotisations-independants.html"
									aria-label={t(
										'pages.assistants.choix-statut.warning.réforme.aria-label',
										'Lire la page dédiée à la réforme de l’assiette et du barème des cotisations sur le site de l’Urssaf, nouvelle fenêtre'
									)}
								>
									réforme de l’assiette et du barème des cotisations
								</Link>{' '}
								n'est pas encore implémentée sur ce comparateur.
							</Trans>
						</Strong>
					</Body>
				</StyledMessage>
			)}

			<Accordion
				variant="light"
				defaultExpandedKeys={expandRevenuSection ? ['revenus'] : []}
				title={
					<H2>
						{t('pages.assistants.choix-statut.accordion-title', 'Comparer…')}
					</H2>
				}
				isFoldable
			>
				<Item
					title={
						<ItemTitle>
							{t(
								'pages.assistants.choix-statut.items.revenus.title',
								'Vos revenus'
							)}
							&nbsp;
							<Emoji emoji="🤑" />
						</ItemTitle>
					}
					key="revenus"
					hasChildItems={false}
					textValue={t(
						'pages.assistants.choix-statut.items.revenus.title',
						'Vos revenus'
					)}
				>
					<H4>
						{t(
							'pages.assistants.choix-statut.items.revenus.h4',
							'Revenu net mensuel après impôts'
						)}

						<InfoButton
							subject={t(
								'pages.assistants.choix-statut.items.revenus.info.subject',
								'le revenu'
							)}
							popoverTitle={t(
								'pages.assistants.choix-statut.items.revenus.info.title',
								'Calculer vos revenus'
							)}
						>
							<RevenuTable namedEngines={namedEngines} />
						</InfoButton>
					</H4>
					<DetailsRowCards
						dottedName="dirigeant . rémunération . net . après impôt"
						namedEngines={namedEngines}
						unit="€/mois"
						footer={(engine) => (
							<Condition
								expression={{
									'est défini': 'dirigeant . rémunération . net . après impôt',
								}}
								engine={engine}
							>
								<Ul>
									<Li>
										<Trans i18nKey="pages.assistants.choix-statut.items.revenus.détails.1">
											Soit{' '}
											<Strong>
												<Value
													engine={engine}
													expression="dirigeant . rémunération . net"
													displayedUnit="€"
													unit="€/mois"
													precision={0}
													linkToRule={false}
												/>
											</Strong>{' '}
											avant impôts
										</Trans>
									</Li>{' '}
									<Condition
										engine={engine}
										expression="dirigeant . rémunération . totale > 0"
									>
										<Li>
											<Condition
												engine={engine}
												expression="dirigeant . auto-entrepreneur"
											>
												<Trans i18nKey="pages.assistants.choix-statut.items.revenus.détails.2">
													Avec{' '}
													<Strong>
														<Value
															engine={engine}
															expression="dirigeant . rémunération . cotisations / entreprise . chiffre d'affaires"
															unit="%"
															precision={0}
															linkToRule={false}
														/>
													</Strong>{' '}
													de cotisations sociales sur le chiffre d’affaires
													(soit{' '}
													<Strong>
														<Value
															engine={engine}
															expression="dirigeant . rémunération . cotisations / dirigeant . rémunération . totale"
															unit="%"
															precision={0}
															linkToRule={false}
														/>
													</Strong>{' '}
													du bénéfice)
												</Trans>
											</Condition>
											<Condition
												engine={engine}
												expression={{
													'=': ['dirigeant . auto-entrepreneur', 'non'],
												}}
											>
												<Trans i18nKey="pages.assistants.choix-statut.items.revenus.détails.3">
													Avec{' '}
													<Strong>
														<Value
															engine={engine}
															expression="dirigeant . rémunération . cotisations / dirigeant . rémunération . totale"
															unit="%"
															precision={0}
															linkToRule={false}
														/>
													</Strong>{' '}
													de cotisations sociales sur le bénéfice
												</Trans>
											</Condition>
										</Li>
									</Condition>
								</Ul>
							</Condition>
						)}
						warning={(engine) => (
							<Condition
								engine={engine}
								expression={{
									et: [
										'entreprise . catégorie juridique . EI . auto-entrepreneur',
										"entreprise . chiffre d'affaires . seuil micro . dépassé",
									],
								}}
							>
								<WarningTooltip
									tooltip={
										<Trans i18nKey="pages.assistants.choix-statut.items.revenus.warning">
											Vous allez dépasser le plafond de la micro-entreprise{' '}
											<span>
												(
												<Value
													linkToRule={false}
													displayedUnit="€"
													expression={
														String(
															engine.evaluate('entreprise . activité . nature')
																.nodeValue
														) === 'libérale' ||
														String(
															engine.evaluate(
																'entreprise . activités . service ou vente'
															).nodeValue
														) === 'service'
															? "entreprise . chiffre d'affaires . seuil micro . libérale"
															: "entreprise . chiffre d'affaires . seuil micro . total"
													}
												/>{' '}
												de chiffre d’affaires).
											</span>
										</Trans>
									}
								/>
							</Condition>
						)}
					/>
				</Item>

				<Item
					title={
						<ItemTitle>
							{t(
								'pages.assistants.choix-statut.items.retraite.title',
								'Vos droits pour la retraite'
							)}
							&nbsp;
							<Emoji emoji="🧐" />
						</ItemTitle>
					}
					key="retraite"
					hasChildItems={false}
					textValue={t(
						'pages.assistants.choix-statut.items.retraite.title',
						'Vos droits pour la retraite'
					)}
				>
					<H4>
						{t(
							'pages.assistants.choix-statut.items.retraite.base.h4',
							'Retraite de base'
						)}
						<ExplicableRule dottedName="protection sociale . retraite . trimestres" />
					</H4>
					<Body>
						<Trans i18nKey="pages.assistants.choix-statut.items.retraite.base.body">
							Chaque année, selon votre rémunération, vous validez{' '}
							<Strong>jusqu'à 4 trimestres</Strong> pour votre retraite de base.
							Le nombre total de trimestres validés détermine votre droit à une
							retraite à taux plein.
						</Trans>
					</Body>

					<DetailsRowCards
						dottedName="protection sociale . retraite . trimestres"
						namedEngines={namedEngines}
						displayedUnit={t(
							'pages.assistants.choix-statut.items.retraite.base.unit',
							'trimestre(s) acquis par an'
						)}
					/>

					<Condition
						expression={{
							'=': [
								'entreprise . activité . nature . libérale . réglementée',
								'non',
							],
						}}
					>
						<Body>
							<Trans i18nKey="pages.assistants.choix-statut.items.retraite.base.revenu-cotisé.body">
								Le montant de votre pension dépend aussi du{' '}
								<Strong>revenu cotisé</Strong>, c'est-à-dire la part de vos
								revenus sur laquelle vous cotisez pour la retraite de base.
							</Trans>
						</Body>

						<DetailsRowCards
							dottedName="protection sociale . retraite . base . cotisée"
							namedEngines={namedEngines}
							unit="€/an"
							displayedUnit="€ cotisés par an"
						/>
					</Condition>

					<H4>
						{t(
							'pages.assistants.choix-statut.items.retraite.complémentaire.h4',
							'Retraite complémentaire'
						)}
						<ExplicableRule dottedName="protection sociale . retraite . complémentaire" />
					</H4>
					<Body>
						<Trans i18nKey="pages.assistants.choix-statut.items.retraite.complémentaire.body">
							Tous les ans, selon votre rémunération,{' '}
							<Strong>vous gagnez des points de retraite complémentaire</Strong>
							. En fin de carrière, vos points sont convertis en pension
							mensuelle qui s'ajoute à votre retraite de base.
						</Trans>
					</Body>

					<DetailsRowCards
						dottedName="protection sociale . retraite . complémentaire . points acquis"
						namedEngines={namedEngines}
						displayedUnit={t(
							'pages.assistants.choix-statut.items.retraite.complémentaire.unit',
							'point(s) acquis par an'
						)}
						footer={(engine) => (
							<BodyNoMargin>
								<Trans i18nKey="pages.assistants.choix-statut.items.retraite.complémentaire.valeur-du-point">
									Valeur du point&nbsp;:{' '}
									<Strong>
										<Value
											engine={engine}
											expression="protection sociale . retraite . complémentaire . valeur du point"
											displayedUnit="€ par an"
											linkToRule={false}
										/>
									</Strong>
								</Trans>
							</BodyNoMargin>
						)}
					/>

					<Message type="info" border={false}>
						<Body>
							<Trans i18nKey="pages.assistants.choix-statut.items.retraite.lien-assurance-retraite.message">
								Pour estimer le montant de votre future pension de retraite,
								utilisez le{' '}
								<Link
									href="https://www.lassuranceretraite.fr/portail-info/hors-menu/annexe/services-en-ligne/estimation-montant-retraite.html"
									aria-label={t(
										'pages.assistants.choix-statut.items.retraite.lien-assurance-retraite.aria-label',
										"Accéder au simulateur de l'Assurance retraite, nouvelle fenêtre"
									)}
								>
									simulateur de l'Assurance retraite
								</Link>
								.
							</Trans>
						</Body>
					</Message>
				</Item>

				<Item
					title={
						<ItemTitle>
							{t(
								'pages.assistants.choix-statut.items.santé.title',
								'Vos prestations santé'
							)}
							&nbsp;
							<Emoji emoji="😷" />
						</ItemTitle>
					}
					key="santé"
					hasChildItems={false}
					textValue={t(
						'pages.assistants.choix-statut.items.santé.title',
						'Vos prestations santé'
					)}
				>
					<Trans i18nKey="pages.assistants.choix-statut.items.santé.body">
						<BodyNoBottomMargin>
							Tous les statuts vous ouvrent le droit au{' '}
							<Strong>remboursement des soins.</Strong>
						</BodyNoBottomMargin>
						<BodyNoMargin>
							Pour tous les statuts, il est conseillé de souscrire à une{' '}
							<Strong>prévoyance complémentaire (mutuelle)</Strong> pour
							améliorer le remboursement des frais de santé.
						</BodyNoMargin>
					</Trans>

					<H4>
						{t(
							'pages.assistants.choix-statut.items.santé.arrêt.h4',
							'Arrêt maladie'
						)}
						<ExplicableRule dottedName="protection sociale . maladie . arrêt maladie" />
					</H4>
					<Body>
						<Trans i18nKey="pages.assistants.choix-statut.items.santé.arrêt.body">
							Pour tous les statuts, vous aurez un{' '}
							<Strong>délai de carence de 3 jours</Strong>. En cas d’arrêt
							maladie, l’assurance maladie vous versera :
						</Trans>
					</Body>
					<DetailsRowCards
						dottedName="protection sociale . maladie . arrêt maladie"
						namedEngines={namedEngines}
						unit="€/jour"
						warning={(engine) => (
							<Condition
								engine={engine}
								expression="protection sociale . maladie . arrêt maladie = 0"
							>
								<WarningTooltip
									tooltip={
										<span
											style={{
												fontWeight: 'normal',
											}}
										>
											<Trans i18nKey="pages.assistants.choix-statut.items.santé.arrêt.warning">
												Votre <Strong>rémunération</Strong> est{' '}
												<Strong>trop faible</Strong> pour bénéficier d’arrêt
												maladie.
											</Trans>
										</span>
									}
								/>
							</Condition>
						)}
						footer={(engine) => (
							<Condition
								engine={engine}
								expression="protection sociale . maladie . arrêt maladie != 0"
							>
								<StyledDiv>
									<CircledPlusIcon />
									<BodyNoMargin>
										<Trans i18nKey="pages.assistants.choix-statut.items.santé.arrêt.footer">
											Pour y prétendre, vous devez avoir cotisé au moins{' '}
											<Strong>
												<Value
													engine={engine}
													expression="protection sociale . maladie . arrêt maladie . délai d'attente"
												/>
											</Strong>
										</Trans>
									</BodyNoMargin>
								</StyledDiv>
							</Condition>
						)}
					/>

					<H4>
						{t(
							'pages.assistants.choix-statut.items.santé.atmp.h4',
							'Accident du travail et maladie professionnelle'
						)}
						<ExplicableRule dottedName="protection sociale . maladie . accidents du travail et maladies professionnelles . indemmnités" />
					</H4>
					<Body>
						<Trans i18nKey="pages.assistants.choix-statut.items.santé.atmp.body">
							En cas d’<Strong>accident du travail</Strong>, de{' '}
							<Strong>maladie professionnelle</Strong> ou d’un{' '}
							<Strong>accident sur le trajet domicile-travail</Strong>, vous
							recevrez une indemnisation de&nbsp;:
						</Trans>
					</Body>
					<DetailsRowCards
						dottedName="protection sociale . maladie . accidents du travail et maladies professionnelles . indemmnités"
						namedEngines={namedEngines}
						unit="€/mois"
						evolutionDottedName="protection sociale . maladie . accidents du travail et maladies professionnelles . indemmnités . à partir du 29ème jour"
						evolutionLabel={t(
							'pages.assistants.choix-statut.items.santé.atmp.evolution-label',
							'à partir du 29ème jour'
						)}
					/>
				</Item>

				<Item
					title={
						<ItemTitle>
							{t(
								'pages.assistants.choix-statut.items.parentalité.title',
								'La maternité, paternité et adoption'
							)}
							&nbsp;
							<Emoji emoji="🤗" />
						</ItemTitle>
					}
					key="enfants"
					hasChildItems={false}
					textValue={t(
						'pages.assistants.choix-statut.items.parentalité.title',
						'La maternité, paternité et adoption'
					)}
				>
					<Trans i18nKey="pages.assistants.choix-statut.items.parentalité.body">
						<BodyNoBottomMargin>
							Tous les statuts vous ouvrent le droit aux{' '}
							<Strong>indemnités journalières</Strong> de congé maternité,
							paternité, adoption.
						</BodyNoBottomMargin>
						<BodyNoTopMargin>
							Pour y prétendre, vous devez avoir cotisé{' '}
							<Strong>au moins 6 mois</Strong>.
						</BodyNoTopMargin>
					</Trans>

					<DetailsRowCards
						dottedName="protection sociale . maladie . maternité paternité adoption"
						namedEngines={namedEngines}
						unit="€/jour"
					/>

					<H4>
						{t(
							'pages.assistants.choix-statut.items.parentalité.maternité.h4',
							'Maternité'
						)}
						<ExplicableRule dottedName="protection sociale . maladie . maternité paternité adoption . allocation forfaitaire de repos maternel" />
					</H4>
					<Body>
						<Trans i18nKey="pages.assistants.choix-statut.items.parentalité.maternité.body">
							En plus des indemnités journalières, vous pouvez aussi prétendre à
							une{' '}
							<Strong>
								allocation forfaitaire de repos maternel supplémentaire
							</Strong>
							.
						</Trans>
					</Body>
					<DetailsRowCards
						dottedName="protection sociale . maladie . maternité paternité adoption . allocation forfaitaire de repos maternel"
						namedEngines={namedEngines}
						label={t(
							'pages.assistants.choix-statut.items.parentalité.maternité.label',
							'versés en deux fois'
						)}
					/>

					<H4>
						{t(
							'pages.assistants.choix-statut.items.parentalité.adoption.h4',
							'Adoption'
						)}
						<ExplicableRule dottedName="protection sociale . maladie . maternité paternité adoption . allocation forfaitaire de repos adoption" />
					</H4>
					<Body>
						<Trans i18nKey="pages.assistants.choix-statut.items.parentalité.adoption.body">
							En plus des indemnités journalières, vous pouvez aussi prétendre à
							une{' '}
							<Strong>
								allocation forfaitaire de repos parental supplémentaire
							</Strong>
							.
						</Trans>
					</Body>
					<DetailsRowCards
						dottedName="protection sociale . maladie . maternité paternité adoption . allocation forfaitaire de repos adoption"
						namedEngines={namedEngines}
						label={t(
							'pages.assistants.choix-statut.items.parentalité.adoption.label',
							'versés en une fois'
						)}
					/>
				</Item>

				<Item
					title={
						<ItemTitle>
							{t(
								'pages.assistants.choix-statut.items.prévoyance.title',
								'Votre couverture invalidité et décès'
							)}
							&nbsp;
							<Emoji emoji="🤕" />
						</ItemTitle>
					}
					key="maladie"
					hasChildItems={false}
					textValue={t(
						'pages.assistants.choix-statut.items.prévoyance.title',
						'Votre couverture invalidité et décès'
					)}
				>
					<Body>
						<Trans i18nKey="pages.assistants.choix-statut.items.prévoyance.body">
							Tous les statuts cotisent pour une{' '}
							<Strong>pension invalidité-décès</Strong> qui vous{' '}
							<Strong>protège en cas d’invalidité</Strong> et assure à vos
							proches une{' '}
							<Strong>
								pension de réversion et un capital en cas de décès
							</Strong>
							.
						</Trans>
					</Body>
					<H4>
						{t(
							'pages.assistants.choix-statut.items.prévoyance.invalidité.h4',
							'Invalidité'
						)}
						<ExplicableRule dottedName="protection sociale . invalidité et décès" />
					</H4>
					<Trans i18nKey="pages.assistants.choix-statut.items.prévoyance.invalidité.body.1">
						<BodyNoMargin>
							Vous pouvez bénéficier d’une pension invalidité{' '}
							<Strong>
								en cas de maladie ou d’accident conduisant à une incapacité à
								poursuivre votre activité professionnelle
							</Strong>
							.
						</BodyNoMargin>
						<BodyNoTopMargin>
							Pour y prétendre, vous devez respecter{' '}
							<BlackColoredLink href="https://www.service-public.fr/particuliers/vosdroits/F672">
								certaines règles
								<StyledExternalLinkIcon />
							</BlackColoredLink>
							.
						</BodyNoTopMargin>
					</Trans>
					<DetailsRowCards
						dottedName="protection sociale . invalidité et décès . pension invalidité . invalidité partielle"
						evolutionDottedName="protection sociale . invalidité et décès . pension invalidité . invalidité totale"
						namedEngines={namedEngines}
						unit="€/mois"
						label={
							<span style={{ fontSize: '1rem' }}>
								{t(
									'pages.assistants.choix-statut.items.prévoyance.invalidité.label',
									'(invalidité partielle)'
								)}
							</span>
						}
						evolutionLabel={
							<span style={{ fontSize: '0.875rem' }}>
								{t(
									'pages.assistants.choix-statut.items.prévoyance.invalidité.evolution-label',
									'(invalidité totale)'
								)}
							</span>
						}
					/>
					<Body>
						<Trans i18nKey="pages.assistants.choix-statut.items.prévoyance.invalidité.body.2">
							Pour une invalidité causée par un{' '}
							<Strong>accident professionnel</Strong>, vous pouvez bénéficier
							d’une <Strong>rente d’incapacité</Strong>.
						</Trans>
					</Body>
					<DetailsRowCards
						dottedName="protection sociale . invalidité et décès . accidents du travail et maladies professionnelles . rente incapacité"
						namedEngines={namedEngines}
						unit="€/mois"
					/>

					<H4>
						{t(
							'pages.assistants.choix-statut.items.prévoyance.décès.h4',
							'Décès'
						)}
						<ExplicableRule dottedName="protection sociale . invalidité et décès . capital décès" />
					</H4>
					<Body>
						<Trans i18nKey="pages.assistants.choix-statut.items.prévoyance.décès.body.1">
							La Sécurité Sociale garantit un{' '}
							<Strong>capital décès pour vos ayants droits</Strong> (personnes
							qui sont à votre charge) sous certaines conditions.
						</Trans>
					</Body>
					<DetailsRowCards
						dottedName="protection sociale . invalidité et décès . capital décès"
						label={t(
							'pages.assistants.choix-statut.items.prévoyance.décès.label.1',
							'pour vos proches'
						)}
						namedEngines={namedEngines}
					/>

					<Body>
						<Trans i18nKey="pages.assistants.choix-statut.items.prévoyance.décès.body.2">
							En plus du capital décès, une{' '}
							<Strong>pension de réversion</Strong> peut être versée à votre
							conjoint/conjointe. Elle correspond aux{' '}
							<Strong>droits à la retraite que vous aurez acquis</Strong> durant
							sa vie professionnelle.
						</Trans>
					</Body>
					<DetailsRowCards
						dottedName="protection sociale . invalidité et décès . pension de reversion"
						label={t(
							'pages.assistants.choix-statut.items.prévoyance.décès.label.2',
							'maximum'
						)}
						namedEngines={namedEngines}
					/>

					<Body>
						<Trans i18nKey="pages.assistants.choix-statut.items.prévoyance.décès.body.3">
							Pour un décès survenu dans le cadre d’un{' '}
							<Strong>accident professionnel</Strong>, votre conjoint/conjointe
							peut bénéficier d’une <Strong>rente de décès</Strong>.
						</Trans>
					</Body>

					<DetailsRowCards
						dottedName="protection sociale . invalidité et décès . accidents du travail et maladies professionnelles . rente décès"
						namedEngines={namedEngines}
						unit="€/mois"
						label={t(
							'pages.assistants.choix-statut.items.prévoyance.décès.label.3',
							'en cas d’accident professionnel'
						)}
					/>

					<Body>
						<Trans i18nKey="pages.assistants.choix-statut.items.prévoyance.décès.body.4">
							En cas de décès, un <Strong>capital « orphelin »</Strong> est
							versé à<Strong> vos enfants</Strong>, sous certaines conditions.
						</Trans>
					</Body>

					<DetailsRowCards
						dottedName="protection sociale . invalidité et décès . capital décès . orphelin"
						namedEngines={namedEngines}
						unit="€/enfant"
					/>
				</Item>

				<Item
					title={
						<ItemTitle>
							{t(
								'pages.assistants.choix-statut.items.gestion.title',
								'La gestion juridique et comptable'
							)}
							&nbsp;
							<Emoji emoji="🤓" />
						</ItemTitle>
					}
					key="administratif"
					hasChildItems={false}
					textValue={t(
						'pages.assistants.choix-statut.items.gestion.title',
						'La gestion juridique et comptable'
					)}
				>
					<H4>
						{t(
							'pages.assistants.choix-statut.items.gestion.création.h4',
							'Coût de création'
						)}
						<ExplicableRule dottedName="entreprise . coût formalités . création" />
					</H4>
					<Body>
						<Trans i18nKey="pages.assistants.choix-statut.items.gestion.création.body">
							Les formalités de création d’une entreprise diffèrent selon les
							statuts et la nature de l’activité. Le calcul se concentre ici sur
							les <Strong>procédures obligatoires</Strong> (immatriculation,
							annonces légales, rédaction des statuts…).
						</Trans>
					</Body>

					<DetailsRowCards
						dottedName="entreprise . coût formalités . création"
						namedEngines={namedEngines}
						displayedUnit="€ HT"
					/>

					<H4>
						{t(
							'pages.assistants.choix-statut.items.gestion.conjoint.h4',
							'Statut du conjoint / de la conjointe'
						)}
					</H4>
					<Body>
						<Trans i18nKey="pages.assistants.choix-statut.items.gestion.conjoint.body">
							Vous êtes marié/mariée, pacsé/pacsée ou en union libre&nbsp;: il
							existe <Strong>3 statuts possibles</Strong> pour votre
							conjoint/conjointe (<Strong>collaborateur/collaboratrice</Strong>,{' '}
							<Strong>associé/associée</Strong> ou{' '}
							<Strong>salarié/salariée</Strong>).
						</Trans>
					</Body>

					<DetailsRowCards
						expression={{
							variations: [
								{
									si: 'entreprise . catégorie juridique . EI . auto-entrepreneur',
									alors: '"Conjoint collaborateur / Conjointe collaboratrice"',
								},
								{
									si: 'entreprise . catégorie juridique . EI',
									alors:
										'"Conjoint collaborateur ou salarié / Conjointe collaboratrice ou salariée"',
								},
								{
									si: {
										'une de ces conditions': [
											'entreprise . catégorie juridique . SARL',
											'entreprise . catégorie juridique . SELARL',
										],
									},
									alors:
										'"Conjoint collaborateur, associé ou salarié / Conjointe collaboratrice, associée ou salariée"',
								},
								{
									sinon:
										'"Conjoint associé ou salarié / Conjointe associée ou salariée"',
								},
							],
						}}
						namedEngines={namedEngines}
					/>
				</Item>
			</Accordion>
		</Container>
	)
}

const StyledMessage = styled(Message)`
	margin-top: ${({ theme }) => theme.spacings.xl};
	margin-bottom: -${({ theme }) => theme.spacings.md};
`

const StyledDiv = styled.div`
	display: flex;
	align-items: center;
	column-gap: ${({ theme }) => theme.spacings.md};

	svg {
		width: 2.5rem;
	}
`

const BodyNoBottomMargin = styled(Body)`
	margin-bottom: 0;
`
const BodyNoTopMargin = styled(Body)`
	margin-top: 0;
`
const BodyNoMargin = styled(Body)`
	margin: 0;
`

const StyledExternalLinkIcon = styled(ExternalLinkIcon)`
	margin-left: 0.25rem;
`

const BlackColoredLink = styled(StyledLink)`
	color: ${({ theme }) =>
		theme.colors.extended.grey[theme.darkMode ? 100 : 800]};
	svg {
		fill: ${({ theme }) =>
			theme.colors.extended.grey[theme.darkMode ? 100 : 800]};
	}

	&:hover {
		color: ${({ theme }) =>
			theme.colors.extended.grey[theme.darkMode ? 400 : 700]};
		svg {
			fill: ${({ theme }) =>
				theme.colors.extended.grey[theme.darkMode ? 400 : 700]};
		}
	}
`
